import { useState, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useApp } from '../context/AppContext';
import { useI18n } from '../context/I18nContext';
import { useVoice } from '../hooks/useVoice';
import useFullscreen from '../hooks/useFullscreen';
import useIsMobile from '../hooks/useIsMobile';
import useVisualViewport from '../hooks/useVisualViewport';
import MindMapModal from './MindMapModal';
import SessionFeedbackModal from './SessionFeedbackModal';
import SpinningLogo from './SpinningLogo';
import LoadingOverlay from './LoadingOverlay';
import { API_BASE } from '../config';
import { announce } from './ScreenReaderAnnouncer';
import { selectVoiceForAgent } from '../utils/voiceGender';

function extractConclusion(text) {
  if (!text) return '';
  const trimmed = text.trim();
  const paragraphs = trimmed.split(/\n\s*\n/).filter(p => p.trim());
  if (paragraphs.length === 0) return trimmed;
  if (paragraphs.length === 1) {
    const p = paragraphs[0];
    return p.length > 250 ? p.slice(0, 250) + '...' : p;
  }
  return paragraphs[paragraphs.length - 1];
}

function cleanTextForSpeech(text) {
  if (!text) return '';
  return text
    .replace(/!\[.*?\]\(.*?\)/g, '') // remove markdown images
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // keep link text only
    .replace(/```[\s\S]*?```/g, '') // remove code blocks
    .replace(/`([^`]+)`/g, '$1') // inline code to text
    .replace(/(\*\*|__|\*|_|~~|#)/g, '') // remove markdown syntax
    .replace(/\n+/g, ' ') // newlines to spaces
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim();
}

const IMAGE_PLACEHOLDER_REGEX = /\[\[COGENCY_IMAGE_PLACEHOLDER alt:::"([^"]*)" prompt:::"([^"]*)"\]\]/g;
const DXF_LINK_REGEX = /\.dxf$/i;

function MarkdownWithDxf({ children, ...props }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a({ href, children: linkChildren, ...rest }) {
          if (href && DXF_LINK_REGEX.test(href)) {
            return (
              <a href={href} download className="cad-download-link" {...rest}>
                📐 {linkChildren}
              </a>
            );
          }
          return <a href={href} {...rest}>{linkChildren}</a>;
        }
      }}
      {...props}
    >
      {children}
    </ReactMarkdown>
  );
}

function parseImagePlaceholders(content) {
  if (!content) return [{ type: 'text', value: '' }];
  const segments = [];
  let lastIndex = 0;
  let match;
  IMAGE_PLACEHOLDER_REGEX.lastIndex = 0;
  while ((match = IMAGE_PLACEHOLDER_REGEX.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }
    const alt = match[1].replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    const prompt = match[2].replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    segments.push({ type: 'placeholder', alt, prompt });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }
  return segments.length > 0 ? segments : [{ type: 'text', value: content }];
}

function MessageRating({ messageIndex, ratings, onRate, t }) {
  const current = ratings[messageIndex];
  return (
    <div className="message-rating">
      <button
        className={`rating-btn ${current === 'agree' ? 'active agree' : ''}`}
        onClick={() => onRate(messageIndex, current === 'agree' ? null : 'agree')}
        title={t('agree') || "D'accord"}
      >
        👍 {current === 'agree' ? (t('agreed') || 'D\'accord') : (t('agree') || 'Agree')}
      </button>
      <button
        className={`rating-btn ${current === 'disagree' ? 'active disagree' : ''}`}
        onClick={() => onRate(messageIndex, current === 'disagree' ? null : 'disagree')}
        title={t('disagree') || 'Pas d\'accord'}
      >
        👎 {current === 'disagree' ? (t('disagreed') || 'Pas d\'accord') : (t('disagree') || 'Disagree')}
      </button>
    </div>
  );
}

function unescapePlaceholder(str) {
  return str.replace(/\\\\/g, '\\').replace(/\\"/g, '"').replace(/\\\]\\\]/g, ']]');
}

export default function ChatArea() {
  const {
    connected, isProcessing, messages, setMessages, sendQuestion, sendChat,
    sessionId, setShowInvite, setCollabOpen, setShowHelp,
    user, mySocketId, socket,
    moderationPrompt, moderationMode, setModerationMode, setModerationPrompt,
    sendIntervention, skipIntervention, sendTypingIntervention,
    interventionError, setInterventionError,
    startNewSession, closeSession, documents, uploadDocument,
    cancelDebate, editChatMessage, deleteChatMessage,
    editHumanIntervention, deleteHumanIntervention,
    logout, agents, personas,
    saveSession, setScreen
  } = useApp();
  const { t, lang } = useI18n();
  const { toggle: toggleFullscreen, request: requestFullscreen } = useFullscreen({ auto: false });
  const isMobileView = useIsMobile();
  const viewport = useVisualViewport();
  const keyboardHeight = typeof window !== 'undefined' && viewport.height > 0
    ? Math.max(0, window.innerHeight - (viewport.offsetTop + viewport.height))
    : 0;
  const [isFullscreen, setIsFullscreen] = useState(!!(document.fullscreenElement || document.webkitFullscreenElement));
  const [input, setInput] = useState('');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);
  const [showMindMap, setShowMindMap] = useState(false);
  const [mindMapData, setMindMapData] = useState(null);
  const [mindMapSource, setMindMapSource] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [generatingMindMap, setGeneratingMindMap] = useState(false);
  const [generatedImages, setGeneratedImages] = useState({}); // messageIndex -> url
  const [imageLoading, setImageLoading] = useState({}); // key -> boolean
  const [quickImageMode, setQuickImageMode] = useState(false);
  const [quickImagePrompt, setQuickImagePrompt] = useState('');
  const [quickImageLoading, setQuickImageLoading] = useState(false);
  const quickImageInputRef = useRef(null);
  const [interventionText, setInterventionText] = useState('');
  const interventionTextRef = useRef(''); // always-synced ref for submit button
  const lastAttemptedRef = useRef(''); // save text in case of rejection
  const [isSubmittingIntervention, setIsSubmittingIntervention] = useState(false);

  // Keep ref in sync so submit always reads latest text
  useEffect(() => { interventionTextRef.current = interventionText; }, [interventionText]);

  // When server rejects the intervention, restore the text and re-open the modal
  useEffect(() => {
    if (interventionError && lastAttemptedRef.current) {
      setInterventionText(lastAttemptedRef.current);
      setHasTyped(true);
      setBigInputMode('intervention');
      setShowBigInput(true);
      setIsSubmittingIntervention(false);
      setInterventionError(null);
      lastAttemptedRef.current = '';
    }
  }, [interventionError]);
  const [ratings, setRatings] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cogency_ratings') || '{}'); }
    catch { return {}; }
  });
  const handleRate = (msgIndex, rating) => {
    const newRatings = { ...ratings, [msgIndex]: rating };
    setRatings(newRatings);
    try { localStorage.setItem('cogency_ratings', JSON.stringify(newRatings)); } catch {}
  };

  const interventionTimerRef = useRef(null);
  const [, setTimeLeft] = useState(0);
  const [hasTyped, setHasTyped] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showBigInput, setShowBigInput] = useState(false);
  const [bigInputMode, setBigInputMode] = useState('question'); // 'question' | 'intervention'
  const bigTextareaRef = useRef(null);
  const [bigInputCursor, setBigInputCursor] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const shouldAutoScrollRef = useRef(true);
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const [showOffTopicModal, setShowOffTopicModal] = useState(false);
  const [pendingInterventionText, setPendingInterventionText] = useState('');
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingFeedbackAction, setPendingFeedbackAction] = useState(null); // 'close' | 'logout'
  const feedbackSubmittedRef = useRef(false);

  // Long-press voice export modal
  const [voiceExportModal, setVoiceExportModal] = useState({ open: false, text: '' });
  const [mp3Loading, setMp3Loading] = useState(false);
  const [wavLoading, setWavLoading] = useState(false);

  const {
    isListening, isSpeaking, transcript, voiceSettings,
    recordingDuration, formatDuration, voices, voicesByLang,
    startListening, stopListening, speak, speakSummary, stopSpeaking,
  } = useVoice();

  const agentVoiceMap = useMemo(() => {
    const map = {};
    if (!agents?.length || !voices?.length) return map;
    agents.forEach((agent, idx) => {
      const persona = personas.find(p => p.id === agent.persona);
      const name = persona?.name || agent.persona || `Agent ${idx + 1}`;
      map[name] = selectVoiceForAgent(name, voices, voicesByLang, voiceSettings?.language || lang);
    });
    return map;
  }, [agents, personas, voices, voicesByLang, voiceSettings?.language, lang]);

  // Voice transcript handling disabled
  // useEffect(() => {
  //   if (transcript) {
  //     setVoiceTranscript(transcript);
  //     setIsProcessingVoice(false);
  //     setTranscript('');
  //   }
  // }, [transcript, setTranscript]);

  useEffect(() => {
    if (!moderationPrompt?.active) {
      setHasTyped(false);
      setInterventionText('');
      setIsSubmittingIntervention(false);
      lastAttemptedRef.current = '';
      if (interventionTimerRef.current) {
        clearTimeout(interventionTimerRef.current);
        interventionTimerRef.current = null;
      }
    }
  }, [moderationPrompt?.active]);

  useEffect(() => {
    const update = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', update);
    document.addEventListener('webkitfullscreenchange', update);
    return () => {
      document.removeEventListener('fullscreenchange', update);
      document.removeEventListener('webkitfullscreenchange', update);
    };
  }, []);

  useEffect(() => {
    if (showBigInput && bigTextareaRef.current && bigInputCursor !== null) {
      bigTextareaRef.current.setSelectionRange(bigInputCursor, bigInputCursor);
    }
  }, [interventionText, input, bigInputMode, bigInputCursor]);

  useEffect(() => {
    if (!moderationPrompt?.active) {
      setTimeLeft(0);
      return;
    }
    setTimeLeft(0);
  }, [moderationPrompt]);

  const scrollToBottom = (force = false) => {
    if (!messagesEndRef.current || !messagesContainerRef.current) return;
    if (force || (shouldAutoScrollRef.current && !isUserScrollingRef.current)) {
      messagesEndRef.current.scrollIntoView({ behavior: force ? 'auto' : 'smooth' });
    }
  };

  const checkNearBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    const threshold = 80;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= threshold;
  };

  const handleScroll = () => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    isUserScrollingRef.current = true;
    const nearBottom = checkNearBottom();
    setIsNearBottom(nearBottom);
    shouldAutoScrollRef.current = nearBottom;
    scrollTimeoutRef.current = setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 1500);
  };

  const scrollToBottomButton = () => {
    shouldAutoScrollRef.current = true;
    setIsNearBottom(true);
    scrollToBottom(true);
  };

  const clearInterventionTimer = () => {
    if (interventionTimerRef.current) {
      clearTimeout(interventionTimerRef.current);
      interventionTimerRef.current = null;
    }
  };

  const startInterventionTimer = () => {
    clearInterventionTimer();
    interventionTimerRef.current = setTimeout(() => {
      setIsSubmittingIntervention(false);
    }, 10000);
  };

  const submitIntervention = async (textOverride) => {
    // Use ref for latest text to avoid stale closure
    const latestText = textOverride !== undefined ? textOverride : interventionTextRef.current;
    const text = latestText.trim();
    console.log('[ChatArea] submitIntervention called', { textLength: text.length, isSubmittingIntervention, moderationActive: moderationPrompt?.active, connected });
    if (!text || isSubmittingIntervention || !moderationPrompt?.active) {
      console.log('[ChatArea] submitIntervention blocked', { reason: !text ? 'empty' : isSubmittingIntervention ? 'submitting' : 'no-moderation' });
      return;
    }

    // Disable button immediately for instant feedback
    setIsSubmittingIntervention(true);
    clearInterventionTimer();

    // Safety: force-reset submitting state after 8s max
    const safetyTimer = setTimeout(() => {
      console.warn('[ChatArea] intervention safety timeout — resetting state');
      setIsSubmittingIntervention(false);
      setShowBigInput(false);
    }, 8000);

    try {
      // Relevance check: fire-and-forget, never block sending
      const token = localStorage.getItem('cogency_token');
      fetch(`${API_BASE}/api/sessions/${sessionId}/check-intervention-relevance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text })
      }).then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && !data.relevant) {
            setPendingInterventionText(text);
            setShowOffTopicModal(true);
          }
        })
        .catch(e => console.warn('[ChatArea] relevance check failed', e.message));

      const result = sendIntervention(text);
      // Save text in case server rejects it (content filter, policy)
      lastAttemptedRef.current = text;
      if (result) {
        setInterventionText('');
        setShowBigInput(false);
        startInterventionTimer();
      } else {
        // sendIntervention returned falsy (socket disconnected?)
        clearTimeout(safetyTimer);
        setIsSubmittingIntervention(false);
        console.warn('[ChatArea] sendIntervention failed — socket may be disconnected');
      }
    } catch (e) {
      console.error('[ChatArea] submitIntervention error', e);
      clearTimeout(safetyTimer);
      setIsSubmittingIntervention(false);
    }
  };

  const savePendingQuestion = async () => {
    if (!pendingInterventionText) return;
    setSavingQuestion(true);
    try {
      const token = localStorage.getItem('cogency_token');
      await fetch(`${API_BASE}/api/saved-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: pendingInterventionText, sessionId })
      });
    } catch (e) {
      console.error('[ChatArea] save question failed', e);
    }
    setSavingQuestion(false);
    setShowOffTopicModal(false);
    setPendingInterventionText('');
    setInterventionText('');
    setShowBigInput(false);
    // Do not submit the intervention; user chose to save for later
  };

  const sendPendingAnyway = () => {
    const text = pendingInterventionText;
    setShowOffTopicModal(false);
    setPendingInterventionText('');
    if (!text) return;
    setIsSubmittingIntervention(true);
    sendIntervention(text);
    setInterventionText('');
    setShowBigInput(false);
    startInterventionTimer();
  };

  const cancelOffTopicModal = () => {
    setShowOffTopicModal(false);
    setPendingInterventionText('');
  };

  const passIntervention = () => {
    if (isSubmittingIntervention || !moderationPrompt?.active) return;
    setIsSubmittingIntervention(true);
    skipIntervention();
    setShowBigInput(false);
    startInterventionTimer();
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (sessionId && !feedbackSubmittedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionId]);

  useEffect(() => {
    if (showBigInput && bigTextareaRef.current && bigInputCursor === null) {
      bigTextareaRef.current.focus();
      const len = bigInputMode === 'intervention' ? interventionText.length : input.length;
      bigTextareaRef.current.setSelectionRange(len, len);
    }
  }, [showBigInput, bigInputMode, bigInputCursor]);

  useEffect(() => {
    if (!showBigInput) setBigInputCursor(null);
  }, [showBigInput]);

  const exportSession = async (format) => {
    if (!sessionId) return;
    setExportLoading(true);
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/sessions/${sessionId}/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'latex' ? 'tex' : format;
      a.download = `cogency-session-${sessionId.slice(-8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('exportFailed') + ': ' + err.message);
    }
    setExportLoading(false);
    setShowExportMenu(false);
  };

  const exportMessageToPPTX = async (title, content) => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/generate-pptx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, content })
      });
      if (!res.ok) throw new Error('PPTX generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = title.replace(/[^a-z0-9\s-]/gi, '').replace(/\s+/g, '-').slice(0, 30).toLowerCase() || 'response';
      a.download = `cogency-${safeName}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('failedToExportPPTX') + ': ' + err.message);
    }
    setExportLoading(false);
  };

  const generateImageDirectly = async (prompt, target) => {
    const key = target?.messageIndex !== undefined
      ? `${target.messageIndex}-${target.placeholderKey || 'inline'}`
      : 'inline';
    if (imageLoading[key]) return;
    const cleanPrompt = (prompt || '').trim();
    if (!cleanPrompt) return;
    setImageLoading(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: cleanPrompt })
      });
      const data = await res.json();
      if (data.success && data.url) {
        handleImageGenerated({ url: data.url, prompt: cleanPrompt, alt: cleanPrompt.slice(0, 120) }, target);
      } else {
        alert((t('failedToGenerateImage') || 'Image generation failed') + (data.error ? `: ${data.error}` : ''));
      }
    } catch (err) {
      alert((t('failedToGenerateImage') || 'Image generation failed') + ': ' + err.message);
    } finally {
      setImageLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleImageGenerated = (result, target) => {
    const { url, prompt, alt } = (typeof result === 'string' ? { url: result, prompt: '', alt: '' } : result) || {};
    if (target?.messageIndex !== undefined) {
      const key = `${target.messageIndex}-${target.placeholderKey || 'inline'}`;
      if (target.replaceMessage) {
        setMessages(prev => prev.map((m, i) => i === target.messageIndex
          ? { ...m, data: { ...m.data, url, prompt: prompt || m.data?.prompt || '', alt: alt || m.data?.alt || '', generatedAt: Date.now() } }
          : m
        ));
      } else {
        setGeneratedImages(prev => ({ ...prev, [key]: url }));
      }
    } else {
      // Insert as a new chat image message
      setMessages(prev => [...prev, {
        type: 'image',
        data: { url, prompt: prompt || '', alt: alt || '', generatedAt: Date.now() }
      }]);
    }
  };

  const handleQuickImageGenerate = async () => {
    const prompt = quickImagePrompt.trim();
    if (!prompt || quickImageLoading) return;
    setQuickImageLoading(true);
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt })
      });
      const data = await res.json();
      if (data.success && data.url) {
        handleImageGenerated({ url: data.url, prompt, alt: prompt.slice(0, 120) }, null);
        setQuickImagePrompt('');
        setQuickImageMode(false);
      } else {
        alert((t('failedToGenerateImage') || 'Image generation failed') + (data.error ? `: ${data.error}` : ''));
      }
    } catch (err) {
      alert((t('failedToGenerateImage') || 'Image generation failed') + ': ' + err.message);
    } finally {
      setQuickImageLoading(false);
    }
  };

  const deleteImageMessage = (index) => {
    setMessages(prev => prev.filter((_, i) => i !== index));
  };

  const regenerateImageMessage = (index) => {
    const msg = messages[index];
    const prompt = msg?.data?.prompt || '';
    generateImageDirectly(prompt, { messageIndex: index, replaceMessage: true });
  };

  const exportImageToPPTX = async (index) => {
    const msg = messages[index];
    if (!msg?.data?.url) return;
    setExportLoading(true);
    try {
      const token = localStorage.getItem('cogency_token');
      const title = msg.data.alt || t('generatedImage') || 'Image';
      const res = await fetch(`${API_BASE}/api/generate-pptx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, content: '', imageUrl: msg.data.url, description: msg.data.prompt || msg.data.alt || '' })
      });
      if (!res.ok) throw new Error('PPTX generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = title.replace(/[^a-z0-9\s-]/gi, '').replace(/\s+/g, '-').slice(0, 30).toLowerCase() || 'image';
      a.download = `cogency-image-${safeName}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('failedToExportPPTX') + ': ' + err.message);
    }
    setExportLoading(false);
  };

  const exportImageToPDF = async (index) => {
    const msg = messages[index];
    if (!msg?.data?.url) return;
    setExportLoading(true);
    try {
      const token = localStorage.getItem('cogency_token');
      const title = msg.data.alt || t('generatedImage') || 'Image';
      const res = await fetch(`${API_BASE}/api/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, imageUrl: msg.data.url, description: msg.data.prompt || msg.data.alt || '' })
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = title.replace(/[^a-z0-9\s-]/gi, '').replace(/\s+/g, '-').slice(0, 30).toLowerCase() || 'image';
      a.download = `cogency-image-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('failedToExportPDF') + ': ' + err.message);
    }
    setExportLoading(false);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const lastSpokenMsgRef = useRef(null);
  const autoPptxProcessedRef = useRef(new Set());
  const autoPythonProcessedRef = useRef(new Set());

  const runAgentPython = async (code) => {
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/run-python`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.stderr || data.error || 'Execution failed');
      }
      return data;
    } catch (err) {
      console.error('[AGENT PYTHON]', err);
      alert(t('pythonExecutionFailed') || 'Python execution failed: ' + err.message);
      return null;
    }
  };

  // Auto-speak agent/final messages when voice output is enabled
  const spokenMessagesRef = useRef(new Set());
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg) return;

    // Auto-generate PPTX when an agent/final message contains the [PPTX] tag
    if ((lastMsg.type === 'agent' || lastMsg.type === 'final') && !autoPptxProcessedRef.current.has(lastMsg)) {
      const content = lastMsg.data.content || '';
      if (content.includes('[PPTX]')) {
        autoPptxProcessedRef.current.add(lastMsg);
        const pptxMatch = content.match(/\[PPTX\]([\s\S]*?)(?:\[\/PPTX\]|$)/);
        const pptxContent = pptxMatch ? pptxMatch[1].trim() : content.replace(/\[PPTX\]/g, '').trim();
        const title = lastMsg.type === 'agent' ? (lastMsg.data.agent || 'Agent') : (t('finalAnswer') || 'Final Answer');
        exportMessageToPPTX(title, pptxContent);
      }
    }

    // Auto-run Python sandbox when an agent/final message contains the [PYTHON] tag
    if ((lastMsg.type === 'agent' || lastMsg.type === 'final') && !autoPythonProcessedRef.current.has(lastMsg)) {
      const content = lastMsg.data.content || '';
      if (content.includes('[PYTHON]')) {
        autoPythonProcessedRef.current.add(lastMsg);
        const pyMatch = content.match(/\[PYTHON\]([\s\S]*?)(?:\[\/PYTHON\]|$)/);
        const pyCode = pyMatch ? pyMatch[1].trim() : content.replace(/\[PYTHON\]/g, '').trim();
        if (pyCode) {
          runAgentPython(pyCode).then(result => {
            if (result && result.files && result.files.length > 0) {
              // Auto-download first generated file
              const file = result.files[0];
              const a = document.createElement('a');
              a.href = `${API_BASE}${file.url}`;
              a.download = file.name;
              document.body.appendChild(a);
              a.click();
              a.remove();
            }
          });
        }
      }
    }

    const isVoiceOutput = voiceSettings?.outputMode === 'voice' || voiceSettings?.outputMode === 'both';
    if (isVoiceOutput && speakSummary && (lastMsg.type === 'agent' || lastMsg.type === 'final') && !spokenMessagesRef.current.has(lastMsg)) {
      spokenMessagesRef.current.add(lastMsg);
      const textToSpeak = lastMsg.type === 'agent' ? extractConclusion(lastMsg.data.content) : lastMsg.data.content;
      const agentName = lastMsg.type === 'agent' ? lastMsg.data.agent : (t('finalAnswer') || 'Synthesizer');
      const agentVoiceId = agentName ? agentVoiceMap[agentName] : undefined;
      if (textToSpeak?.trim()) speakSummary(textToSpeak, { voiceId: agentVoiceId, agentId: agentName });
    }

    if (lastMsg.type === 'agent') {
      const textToSpeak = lastMsg.data.content;
      announce(`${lastMsg.data.agent} says: ${textToSpeak.slice(0, 120)}`);
    } else if (lastMsg.type === 'final') {
      announce(t('finalAnswerReceived') || 'Final answer received.');
    } else if (lastMsg.type === 'status' || lastMsg.type === 'moderation-prompt') {
      const text = lastMsg.data?.i18nKey ? t(lastMsg.data.i18nKey, lastMsg.data.params) : (lastMsg.data?.message || '');
      announce(text);
    } else if (lastMsg.type === 'error') {
      announce('Error: ' + lastMsg.data.content, 'assertive');
    } else if (lastMsg.type === 'user') {
      announce('You asked: ' + lastMsg.data.content.slice(0, 120));
    }
  }, [messages, voiceSettings?.outputMode, agentVoiceMap, t, speakSummary]);

  const generateMindMap = async (text) => {
    if (generatingMindMap) return;
    setGeneratingMindMap(true);
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/mindmap`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text, model: 'claude:claude-opus-4-8' })
      });
      const data = await res.json();
      if (data.success) {
        setMindMapData(data.mindMap);
        setMindMapSource(text);
        setShowMindMap(true);
      } else {
        alert(t('failedToGenerateMindMap') + ': ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error generating mind map: ' + err.message);
    } finally {
      setGeneratingMindMap(false);
    }
  };

  const handleEditQuestion = (content, messageIndex) => {
    // Annuler le processus des agents IA s'il est en cours
    if (isProcessing) {
      cancelDebate();
    }
    // Supprimer la question et tous les messages suivants pour repartir de cette question
    setMessages(prev => prev.slice(0, messageIndex));
    // Pré-remplir la zone de saisie avec le texte de la question
    setInput(content);
    // Ouvrir le grand champ de saisie pour faciliter l'édition
    setBigInputMode('question');
    setShowBigInput(true);
    setBigInputCursor(null);
  };

  const requestFeedback = (action) => {
    if (feedbackSubmittedRef.current) {
      if (action === 'close') closeSession();
      else if (action === 'logout') logout();
      return;
    }
    setPendingFeedbackAction(action);
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmitted = () => {
    feedbackSubmittedRef.current = true;
    setShowFeedbackModal(false);
    const action = pendingFeedbackAction;
    setPendingFeedbackAction(null);
    if (action === 'close') closeSession();
    else if (action === 'logout') logout();
  };

  const handleFeedbackSkipped = () => {
    feedbackSubmittedRef.current = true;
    setShowFeedbackModal(false);
    const action = pendingFeedbackAction;
    setPendingFeedbackAction(null);
    if (action === 'close') closeSession();
    else if (action === 'logout') logout();
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    stopSpeaking();
    if (isProcessing && sessionId) {
      const messageId = sendChat(text);
      if (messageId) {
        setMessages(prev => [...prev, {
          type: 'chat',
          data: { text, socketId: mySocketId, from: 'You', messageId, time: Date.now() }
        }]);
      }
    } else {
      sendQuestion(text);
    }
    setInput('');
  };

  const handleVoiceSend = () => {
    if (!voiceTranscript.trim() || isProcessing) return;
    sendQuestion(voiceTranscript.trim());
    setVoiceTranscript('');
  };

  const handleVoiceCancel = () => {
    setVoiceTranscript('');
    setIsProcessingVoice(false);
  };

  const handleSpeak = (text, agentName) => {
    if (!text || !speakSummary) return;
    const agentVoiceId = agentName ? agentVoiceMap[agentName] : undefined;
    speakSummary(text, { force: true, voiceId: agentVoiceId, agentId: agentName });
  };

  const handleSpeakLongPress = (text) => {
    if (!text) return;
    setVoiceExportModal({ open: true, text });
  };

  function getSummaryLevel() {
    try {
      const saved = localStorage.getItem('cogency_voice_summary_level');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  }

  function cleanTextForExport(text) {
    return cleanTextForSpeech(text).slice(0, 5000);
  }

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const exportVoiceMP3 = async () => {
    const text = cleanTextForExport(voiceExportModal.text);
    if (!text) return;
    setMp3Loading(true);
    try {
      const token = localStorage.getItem('cogency_token') || '';
      const summaryLevel = getSummaryLevel();
      const res = await fetch(`${API_BASE}/api/voice/tts/mp3`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text,
          options: {
            summarize: summaryLevel > 0,
            summaryLevel
          }
        })
      });
      if (!res.ok) throw new Error('TTS MP3 failed');
      const blob = await res.blob();
      downloadBlob(blob, `cogency-voice-${Date.now()}.mp3`);
    } catch (err) {
      console.error('Export MP3 failed:', err);
      alert(t('voiceExportFailed') || 'Échec de l\'export vocal');
    } finally {
      setMp3Loading(false);
      setVoiceExportModal({ open: false, text: '' });
    }
  };

  const exportVoiceWAV = async () => {
    const text = cleanTextForExport(voiceExportModal.text);
    if (!text) return;
    setWavLoading(true);
    try {
      const token = localStorage.getItem('cogency_token') || '';
      const summaryLevel = getSummaryLevel();
      const res = await fetch(`${API_BASE}/api/voice/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text,
          options: {
            summarize: summaryLevel > 0,
            summaryLevel
          }
        })
      });
      if (!res.ok) throw new Error('TTS WAV failed');
      const blob = await res.blob();
      downloadBlob(blob, `cogency-voice-${Date.now()}.wav`);
    } catch (err) {
      console.error('Export WAV failed:', err);
      alert(t('voiceExportFailed') || 'Échec de l\'export vocal');
    } finally {
      setWavLoading(false);
      setVoiceExportModal({ open: false, text: '' });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    await uploadDocument(file);
    setUploadingDoc(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
      setIsProcessingVoice(true);
    } else {
      startListening();
      setVoiceTranscript('');
      setIsProcessingVoice(false);
    }
  };

  const statusMsg = messages.filter(m => m.type === 'status').slice(-1)[0]?.data;
  const progressPct = statusMsg && statusMsg.totalRounds
    ? Math.round((statusMsg.round / (statusMsg.totalRounds + 1)) * 100)
    : 0;
  const phaseLabels = {
    0: t('initialAnalysis') || 'Initial Analysis',
    1: t('debateRound1') || 'Debate Round 1',
    2: t('debateRound2') || 'Debate Round 2',
    final: t('finalAnswer')
  };

  const providerNames = {
    claude: 'Claude',
    kimi: 'Kimi',
    hf: 'HuggingFace',
    zai: 'Z.ai',
    deepseek: 'DeepSeek',
    xai: 'Grok',
    chatlyai: 'ChatlyAI',
    tchadgpt: 'TchadGPT',
    ollama: 'Ollama',
    openrouter: 'OpenRouter'
  };

  const getAgentDisplayName = (agentId, agentIndex) => {
    const agent = agents.find((a, i) => (a.persona === agentId || i === agentIndex));
    if (!agent?.model) return null;
    const provider = agent.model.split(':')[0];
    return providerNames[provider] || provider;
  };

  const hasFinal = messages.some(m => m.type === 'final');

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + / = focus input
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        const inputEl = document.querySelector('.input-box input:not([disabled])');
        if (inputEl) inputEl.focus();
      }
      // Escape = stop voice or cancel moderation
      if (e.key === 'Escape') {
        if (isListening) handleVoiceToggle();
        if (moderationPrompt?.active) passIntervention();
      }
      // NOTE: Enter is intentionally not bound to send; only the Send button sends the question.
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isListening, input, isProcessing, moderationPrompt, handleVoiceToggle, passIntervention]);

  return (
    <div className="chat-area">
      <div className="chat-header">
        <div className="chat-header-top-row">
          <SpinningLogo size={44} />
          <div className="header-info">
            <h3>{t('cogency')}</h3>
            <span className={`connection-status ${connected ? 'connected' : 'disconnected'}`} title={user?.email || ''}>
              {connected
                ? `● ${user?.name || ''} (${user?.role || ''})`.trim()
                : `○ ${t('disconnected')}`}
            </span>
          </div>
        </div>
        <div className="chat-header-actions-row">
          <button className="icon-btn" onClick={() => setShowHelp(true)} title={t('help')} aria-label={t('help')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </button>
          <button className="icon-btn" onClick={toggleFullscreen} title={isFullscreen ? (t('exitFullscreen') || 'Quitter le plein écran') : (t('fullscreen') || 'Plein écran')} aria-label={isFullscreen ? (t('exitFullscreen') || 'Quitter le plein écran') : (t('fullscreen') || 'Plein écran')}>
            {isFullscreen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3v3a2 2 0 01-2 2H3M8 21v-3a2 2 0 00-2-2H3M16 3v3a2 2 0 002 2h3M16 21v-3a2 2 0 012-2h3"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 00-2 2v3M8 21H5a2 2 0 01-2-2v-3M16 3h3a2 2 0 012 2v3M16 21h3a2 2 0 002-2v-3"/>
              </svg>
            )}
          </button>
          <button
            className={`icon-btn mode-toggle-icon-btn ${moderationMode}`}
            onClick={() => setModerationMode(moderationMode === 'bypass' ? 'present' : 'bypass')}
            title={moderationMode === 'bypass' ? t('modeBypass') : t('modePresent')}
            aria-label={moderationMode === 'bypass' ? t('modeBypass') : t('modePresent')}
          >
            {moderationMode === 'bypass' ? '⚡' : '🛡️'}
          </button>
          <button className="icon-btn" onClick={() => setShowInvite(true)} title={t('invite')} aria-label={t('invite')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
          </button>
          <button className="icon-btn" onClick={() => setCollabOpen(true)} title={t('collaborate')} aria-label={t('collaborate')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
            </svg>
          </button>
          <button className="icon-btn" onClick={startNewSession} title={t('newSession')} aria-label={t('newSession')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </button>
          <button className="icon-btn" onClick={saveSession} title={t('save')} aria-label={t('save')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
          </button>
          <button className="icon-btn" onClick={() => setScreen('history')} title={t('sessionHistory')} aria-label={t('sessionHistory')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="12 8 12 12 16 14"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
          </button>
          {isProcessing && (
            <button className="icon-btn cancel-debate-btn" onClick={cancelDebate} title={t('cancelDebate')} aria-label={t('cancelDebate')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </button>
          )}
          {sessionId && (
            <div className="export-menu-wrapper">
              <button className="icon-btn" onClick={() => setShowExportMenu(v => !v)} title={t('export') || 'Export'} aria-label={t('export') || 'Export'}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </button>
              {showExportMenu && (
                <div className="export-dropdown">
                  <button onClick={() => exportSession('pdf')}>{t('exportPDF') || 'PDF'}</button>
                  <button onClick={() => exportSession('docx')}>{t('exportDOCX') || 'DOCX'}</button>
                  <button onClick={() => exportSession('pptx')}>{t('exportPPTX') || 'PPTX'}</button>
                  <button onClick={() => exportSession('latex')}>{t('exportLaTeX') || 'LaTeX'}</button>
                </div>
              )}
            </div>
          )}
          <button className="icon-btn" onClick={() => requestFeedback('close')} title={t('closeSession') || 'Fermer session'} aria-label={t('closeSession') || 'Fermer session'}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 15v4a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v4"/>
              <polyline points="17 11 12 6 7 11"/>
              <line x1="12" y1="6" x2="12" y2="21"/>
            </svg>
          </button>
          <button className="icon-btn" onClick={() => requestFeedback('logout')} title={t('logout')} aria-label={t('logout')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </div>

      {isProcessing && (
        <div className="progress-bar-container" role="status" aria-live="polite" aria-label={t('processing')}>
          <div className="progress-bar" style={{ width: `${progressPct}%` }}></div>
          <span className="progress-label">
            {statusMsg
              ? (statusMsg.i18nKey ? t(statusMsg.i18nKey, statusMsg.params) : `${phaseLabels[statusMsg.phase] || statusMsg.phase} — ${t('round')} ${statusMsg.round}/${statusMsg.totalRounds}`)
              : t('processing')}
          </span>
        </div>
      )}

      <div
        className="messages-container"
        ref={messagesContainerRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        {messages.length === 0 && (
          <div className="welcome-message">
            <div className="welcome-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
            </div>
            <h2>{t('welcome')}</h2>
            <p>{t('startAsking')}</p>
          </div>
        )}
        {messages.map((msg, idx) => {
          if (msg.type === 'user') return (
            <UserMessage
              key={idx}
              content={msg.data.content}
              canEdit={true}
              onEditQuestion={() => handleEditQuestion(msg.data.content, idx)}
            />
          );
          if (msg.type === 'agent') {
            const displayName = getAgentDisplayName(msg.data.agent, msg.data.agentIndex) || msg.data.agent;
            return (
              <AgentMessage
                key={idx}
                messageIndex={idx}
                data={msg.data}
                displayName={displayName}
                phaseLabels={phaseLabels}
                onSpeak={handleSpeak}
                onSpeakLongPress={handleSpeakLongPress}
                voiceSettings={voiceSettings}
                onExportPPTX={exportMessageToPPTX}
                generatedImages={generatedImages}
                onGenerateImage={(prompt, target) => { generateImageDirectly(prompt, target || { messageIndex: idx }); }}
                ratings={ratings}
                onRate={handleRate}
              />
            );
          }
          if (msg.type === 'agent-stream') {
            const displayName = getAgentDisplayName(msg.data.agent, msg.data.agentIndex) || msg.data.name || msg.data.agent;
            return <StreamingAgentMessage key={idx} data={msg.data} displayName={displayName} phaseLabels={phaseLabels} />;
          }
          if (msg.type === 'final') return (
            <FinalAnswer
              key={idx}
              messageIndex={idx}
              data={msg.data}
              phaseLabels={phaseLabels}
              onSpeak={handleSpeak}
              onSpeakLongPress={handleSpeakLongPress}
              voiceSettings={voiceSettings}
              onExportPPTX={exportMessageToPPTX}
              generatedImages={generatedImages}
              onGenerateImage={(prompt, target) => { generateImageDirectly(prompt, target || { messageIndex: idx }); }}
              ratings={ratings}
              onRate={handleRate}
            />
          );
          if (msg.type === 'error') return <ErrorMessage key={idx} content={msg.data.content} />;
          if (msg.type === 'image') return (
            <ImageMessage
              key={idx}
              url={msg.data.url}
              prompt={msg.data.prompt}
              alt={msg.data.alt}
              onDelete={() => deleteImageMessage(idx)}
              onRegenerate={() => regenerateImageMessage(idx)}
              onExportPPTX={() => exportImageToPPTX(idx)}
              onExportPDF={() => exportImageToPDF(idx)}
            />
          );
          if (msg.type === 'chat') {
            const isOwn = msg.data?.socketId ? msg.data.socketId === mySocketId : msg.data?.from === 'You';
            return (
              <ChatMessage
                key={idx}
                data={msg.data}
                isOwn={isOwn}
                isEditing={editingId === msg.data?.messageId}
                editText={editText}
                onEditStart={() => { setEditingId(msg.data?.messageId); setEditText(msg.data?.text || ''); }}
                onEditChange={setEditText}
                onEditSave={() => { editChatMessage(msg.data?.messageId, editText); setEditingId(null); setEditText(''); }}
                onEditCancel={() => { setEditingId(null); setEditText(''); }}
                onDelete={() => deleteChatMessage(msg.data?.messageId)}
              />
            );
          }
          if (msg.type === 'moderation') return <ModerationMessage key={idx} data={msg.data} />;
          if (msg.type === 'human-intervention') {
            const isOwn = msg.data?.socketId ? msg.data.socketId === mySocketId : false;
            return (
              <HumanInterventionMessage
                key={idx}
                data={msg.data}
                isOwn={isOwn}
                isEditing={editingId === msg.data?.interventionId}
                editText={editText}
                onEditStart={() => { setEditingId(msg.data?.interventionId); setEditText(msg.data?.text || ''); }}
                onEditChange={setEditText}
                onEditSave={() => { editHumanIntervention(msg.data?.interventionId, editText); setEditingId(null); setEditText(''); }}
                onEditCancel={() => { setEditingId(null); setEditText(''); }}
                onDelete={() => deleteHumanIntervention(msg.data?.interventionId)}
              />
            );
          }
          return null;
        })}
        <div ref={messagesEndRef} />
      </div>

      {!isNearBottom && (
        <button
          className="scroll-to-bottom-btn"
          onClick={scrollToBottomButton}
          aria-label={t('scrollToBottom') || 'Scroll to bottom'}
          title={t('scrollToBottom') || 'Scroll to bottom'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 5v14M19 12l-7 7-7-7" />
          </svg>
        </button>
      )}

      <div className="input-section">
          {documents.length > 0 && (
            <div className="attached-documents">
              {documents.map((doc, i) => (
                <div key={i} className="doc-chip" title={doc.extractedText?.slice(0, 200)}>
                  <span className="doc-icon">📄</span>
                  <span className="doc-name">{doc.filename}</span>
                </div>
              ))}
            </div>
          )}
          {quickImageMode && (
            <div className="quick-image-bar">
              <span className="quick-image-icon">🖼️</span>
              <input
                ref={quickImageInputRef}
                type="text"
                className="quick-image-input"
                placeholder={t('describeImageToGenerate') || 'Décrivez l\'image à générer…'}
                value={quickImagePrompt}
                onChange={e => setQuickImagePrompt(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleQuickImageGenerate();
                  } else if (e.key === 'Escape') {
                    setQuickImageMode(false);
                    setQuickImagePrompt('');
                  }
                }}
                disabled={quickImageLoading}
                autoComplete="off"
              />
              <button
                className="quick-image-generate-btn"
                onClick={handleQuickImageGenerate}
                disabled={quickImageLoading || !quickImagePrompt.trim()}
              >
                {quickImageLoading ? (t('generating') || 'Génération…') : (t('generate') || 'Générer')}
              </button>
              <button
                className="quick-image-close-btn"
                onClick={() => { setQuickImageMode(false); setQuickImagePrompt(''); }}
                disabled={quickImageLoading}
                aria-label={t('close') || 'Fermer'}
              >
                ✕
              </button>
            </div>
          )}
          {isListening || isProcessingVoice || voiceTranscript ? (
            <div className="voice-recording-bar">
              {isListening ? (
                <>
                  <div className="voice-recording-indicator">
                    <span className="voice-recording-dot"></span>
                    <span className="voice-recording-time">{formatDuration(recordingDuration)}</span>
                  </div>
                  <span className="voice-recording-label">{t('listeningSpeakNow')}</span>
                  <button className="voice-stop-btn" onClick={handleVoiceToggle} aria-label={t('stop')}>⏹ {t('stop')}</button>
                </>
              ) : isProcessingVoice ? (
                <>
                  <div className="voice-recording-indicator">
                    <span className="voice-processing-spinner">⏳</span>
                  </div>
                  <span className="voice-recording-label">{t('processingYourVoice')}</span>
                  <button className="voice-cancel-btn" onClick={handleVoiceCancel}>✕ Cancel</button>
                </>
              ) : voiceTranscript ? (
                <>
                  <div className="voice-transcript-preview">
                    <span className="voice-transcript-text">{voiceTranscript}</span>
                  </div>
                  <div className="voice-action-buttons">
                    <button className="voice-cancel-btn" onClick={handleVoiceCancel} aria-label={t('cancel')}>✕ {t('cancel')}</button>
                    <button className="voice-send-btn" onClick={handleVoiceSend} aria-label={t('send')}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                      </svg>
                      {t('send')}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <div className="input-box">
              {moderationPrompt?.active ? (
                moderationPrompt.isExhaustionProposal ? (
                  <div className="exhaustion-vote-box">
                    <div className="exhaustion-vote-message">{moderationPrompt.message}</div>
                    <div className="exhaustion-vote-buttons">
                      <button
                        className="btn-primary btn-large"
                        style={{ background: '#10b981', flex: 1 }}
                        onClick={() => {
                          socket?.emit('exhaustion-vote', { sessionId, vote: 'yes' });
                          setModerationPrompt(null);
                        }}
                      >
                        ✅ Oui, clôturer
                      </button>
                      <button
                        className="btn-secondary btn-large"
                        style={{ flex: 1 }}
                        onClick={() => {
                          socket?.emit('exhaustion-vote', { sessionId, vote: 'no' });
                          setModerationPrompt(null);
                        }}
                      >
                        ❌ Non, continuer
                      </button>
                    </div>
                  </div>
                ) : (
                <>
                  <input
                    type="text"
                    placeholder="Enter your intervention..."
                    value={interventionText}
                    onChange={e => {
                      setInterventionText(e.target.value);
                      if (!hasTyped && e.target.value.length > 0) {
                        setHasTyped(true);
                        sendTypingIntervention();
                      }
                    }}
                    onFocus={() => {
                      if (!isSubmittingIntervention) {
                        setBigInputMode('intervention');
                        setShowBigInput(true);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key !== 'Enter' || e.shiftKey) return;
                      e.preventDefault();
                      if (interventionText.trim()) {
                        submitIntervention();
                      } else {
                        passIntervention();
                      }
                    }}
                    disabled={isSubmittingIntervention}
                    autoComplete="off"
                  />
                  <button className="btn-skip" onClick={passIntervention} disabled={isSubmittingIntervention} title={t('noIntervention')} aria-label={t('noIntervention')}>
                    {isSubmittingIntervention ? '…' : t('pass')}
                  </button>
                  <button onClick={submitIntervention} disabled={isSubmittingIntervention || !interventionText.trim()}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                    </svg>
                  </button>
                </>
                )
              ) : (
                <>
                  <input
                    type="text"
                    placeholder={t('askAnything')}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onFocus={() => { setBigInputMode('question'); setShowBigInput(true); }}
                    autoComplete="off"
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                    accept=".txt,.md,.csv,.json,.js,.py,.html,.css,.xml,.yaml,.yml,.pdf,.docx,.png,.jpg,.jpeg,.gif,.webp"
                  />
                  <button
                    className="icon-btn upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessing || uploadingDoc}
                    title={t('attachDocument')}
                    aria-label={t('attachDocument')}
                  >
                    {uploadingDoc ? '⏳' : '📎'}
                  </button>
                  <button
                    className={`icon-btn image-gen-btn ${quickImageMode ? 'active' : ''}`}
                    onClick={() => {
                      setQuickImageMode(v => !v);
                      setTimeout(() => quickImageInputRef.current?.focus(), 0);
                    }}
                    disabled={isProcessing}
                    title={t('generateImage') || 'Générer une image / schéma'}
                    aria-label={t('generateImage') || 'Générer une image / schéma'}
                  >
                    🖼️
                  </button>
                  <button onClick={handleSend}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                    </svg>
                  </button>
                </>
              )}
            </div>
          )}
          <p className="input-hint">
            {t('useSendButton')} &bull; 
            {voiceSettings?.outputMode === 'voice' ? ` ${t('voiceOutputOn')}` : 
             voiceSettings?.outputMode === 'both' ? ` ${t('textVoiceOutput')}` : ` ${t('textOutputOnly')}`}
          </p>
        </div>

    {showBigInput && (
      <div className="big-input-overlay" onClick={e => e.target === e.currentTarget && setShowBigInput(false)}>
        <div
          className="big-input-modal"
          style={keyboardHeight > 0 ? { marginBottom: keyboardHeight } : undefined}
        >
          <div className="big-input-header">
            <h4>{bigInputMode === 'intervention' ? (t('yourIntervention') || 'Votre intervention') : t('askAnything')}</h4>
            <button className="icon-btn" onClick={() => setShowBigInput(false)} aria-label={t('close')}>✕</button>
          </div>
          <textarea
            ref={bigTextareaRef}
            className="big-input-textarea"
            placeholder={bigInputMode === 'intervention' ? (t('enterIntervention') || 'Saisissez votre intervention...') : t('askAnything')}
            value={bigInputMode === 'intervention' ? interventionText : input}
            onChange={e => {
              const newValue = e.target.value;
              const newCursor = e.target.selectionStart;
              if (bigInputMode === 'intervention') {
                setInterventionText(newValue);
                setBigInputCursor(newCursor);
                if (!hasTyped && newValue.length > 0) {
                  setHasTyped(true);
                  sendTypingIntervention();
                }
              } else {
                setInput(newValue);
                setBigInputCursor(newCursor);
              }
            }}
            onKeyUp={e => {
              if (bigInputMode === 'intervention') {
                setBigInputCursor(e.target.selectionStart);
              } else {
                setBigInputCursor(e.target.selectionStart);
              }
            }}
            onClick={e => {
              if (bigInputMode === 'intervention') {
                setBigInputCursor(e.target.selectionStart);
              } else {
                setBigInputCursor(e.target.selectionStart);
              }
            }}
            disabled={bigInputMode === 'intervention' ? isSubmittingIntervention : isProcessing}
            style={keyboardHeight > 0 ? { maxHeight: Math.max(120, viewport.height - 210) } : undefined}
          />
          <div className="big-input-footer">
            <div className="big-input-footer-left">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                accept=".txt,.md,.csv,.json,.js,.py,.html,.css,.xml,.yaml,.yml,.pdf,.docx,.png,.jpg,.jpeg,.gif,.webp"
              />
              <button
                className="icon-btn upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingDoc || (bigInputMode === 'intervention' ? isSubmittingIntervention : isProcessing)}
                title={t('attachDocument')}
                aria-label={t('attachDocument')}
              >
                {uploadingDoc ? '⏳' : '📎'}
              </button>
              <span className="big-input-hint">{t('useSendButton')} &bull; Entrée {t('newLine') || 'nouvelle ligne'}</span>
            </div>
            <div className="big-input-actions">
              <button className="btn-secondary btn-small" onClick={() => setShowBigInput(false)}>{t('cancel')}</button>
              {bigInputMode === 'intervention' && (
                <button className="btn-secondary btn-small" onClick={passIntervention} disabled={isSubmittingIntervention}>
                  {t('pass')}
                </button>
              )}
              <button
                className="btn-primary btn-small"
                onClick={() => {
                  if (bigInputMode === 'intervention' || moderationPrompt?.active) {
                    submitIntervention(interventionText);
                  } else if (input.trim()) {
                    handleSend();
                    setShowBigInput(false);
                  }
                }}
                disabled={bigInputMode === 'intervention' ? (isSubmittingIntervention || !interventionText.trim()) : (isProcessing || !input.trim())}
              >
                {t('send')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    <LoadingOverlay isOpen={exportLoading} label={t('generatingDocument') || 'Génération du document…'} />

    <MindMapModal 
      isOpen={showMindMap} 
      onClose={() => setShowMindMap(false)} 
      mindMapData={mindMapData}
      sourceText={mindMapSource}
    />

    {showOffTopicModal && (
      <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div className="modal" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '520px', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
          <h3 style={{ margin: '0 0 0.75rem' }}>🤔 {t('offTopicIntervention') || 'Intervention hors sujet'}</h3>
          <p style={{ fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1rem' }}>
            {t('offTopicInterventionText') || 'Cette intervention semble ne pas être en lien direct avec la thématique débattue.'}
          </p>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
            "{pendingInterventionText}"
          </div>
          <p style={{ fontSize: '0.9rem', marginBottom: '1.25rem' }}>
            {t('saveQuestionForLater') || 'Souhaitez-vous mémoriser cette question pour une session ultérieure dédiée ?'}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="btn-ghost btn-small" onClick={cancelOffTopicModal} disabled={savingQuestion}>
              {t('cancel') || 'Annuler'}
            </button>
            <button className="btn-secondary btn-small" onClick={sendPendingAnyway} disabled={savingQuestion}>
              {t('sendAnyway') || 'Envoyer quand même'}
            </button>
            <button className="btn-primary btn-small" onClick={savePendingQuestion} disabled={savingQuestion}>
              {savingQuestion ? (t('saving') || 'Sauvegarde…') : (t('saveForLater') || 'Mémoriser')}
            </button>
          </div>
        </div>
      </div>
    )}

    {voiceExportModal.open && (
      <div className="modal-overlay show" onClick={() => setVoiceExportModal({ open: false, text: '' })}>
        <div className="modal voice-export-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{t('exportVoiceTitle') || 'Exporter la lecture vocale'}</h3>
            <button className="modal-close" onClick={() => setVoiceExportModal({ open: false, text: '' })}>✕</button>
          </div>
          <div className="modal-body">
            <p>{t('exportVoiceDesc') || 'Choisissez le format de téléchargement.'}</p>
            <div className="voice-export-actions">
              <button className="btn-secondary" onClick={exportVoiceMP3} disabled={mp3Loading || wavLoading}>
                {mp3Loading ? (t('loading') || 'Chargement…') : (t('exportMP3') || 'Télécharger MP3')}
              </button>
              <button className="btn-secondary" onClick={exportVoiceWAV} disabled={mp3Loading || wavLoading}>
                {wavLoading ? (t('loading') || 'Chargement…') : (t('exportWAV') || 'Télécharger WAV')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showFeedbackModal && (
      <SessionFeedbackModal
        isOpen={showFeedbackModal}
        sessionId={sessionId}
        onSubmitted={handleFeedbackSubmitted}
        onSkip={handleFeedbackSkipped}
      />
    )}
    </div>
  );
}

function UserMessage({ content, onEditQuestion, canEdit }) {
  const { t } = useI18n();
  return (
    <div className="message user-message">
      <div className="message-avatar user-avatar">{t('you')}</div>
      <div className="message-content">
        <MarkdownWithDxf>{content}</MarkdownWithDxf>
        {canEdit && (
          <div className="user-message-actions">
            <button
              className="msg-action-btn edit-question-btn"
              onClick={onEditQuestion}
              title={t('editQuestion') || 'Modifier ma question'}
              aria-label={t('editQuestion') || 'Modifier ma question'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span>{t('editQuestion') || 'Modifier'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RenderContent({ content, messageIndex, generatedImages, onGenerateImage, t }) {
  const segments = parseImagePlaceholders(content);
  const hasArabic = /[\u0600-\u06FF]/.test(content || '');
  const wrapperProps = hasArabic ? { lang: 'ar', dir: 'rtl', className: 'arabic-content' } : {};
  const requestedRef = useRef(new Set());
  useEffect(() => {
    segments.forEach((seg, idx) => {
      if (seg.type !== 'placeholder') return;
      const placeholderKey = `${idx}`;
      const key = `${messageIndex}-${placeholderKey}`;
      if (generatedImages?.[key]) return;
      if (requestedRef.current.has(key)) return;
      requestedRef.current.add(key);
      onGenerateImage(seg.prompt, { messageIndex, placeholderKey });
    });
  }, [segments, messageIndex, generatedImages, onGenerateImage]);
  return (
    <div {...wrapperProps}>
      {segments.map((seg, idx) => {
        if (seg.type === 'text') {
          return <MarkdownWithDxf key={idx}>{seg.value}</MarkdownWithDxf>;
        }
        const placeholderKey = `${idx}`;
        const generatedUrl = generatedImages?.[`${messageIndex}-${placeholderKey}`];
        if (generatedUrl) {
          return <img key={idx} src={generatedUrl} alt={seg.alt || 'Generated image'} className="generated-image" />;
        }
        return (
          <span key={idx} className="image-placeholder-generating" title={t('generating') || 'Génération…'}>
            🖼️ {t('generating') || 'Génération…'}
            {seg.alt && <span className="image-placeholder-alt"> — {seg.alt}</span>}
          </span>
        );
      })}
    </div>
  );
}

function AgentMessage({ data, displayName, phaseLabels, onSpeak, onSpeakLongPress, voiceSettings, onExportPPTX, messageIndex, generatedImages, onGenerateImage, ratings, onRate }) {
  const { t } = useI18n();
  const isVoiceOnly = voiceSettings?.outputMode === 'voice';
  const displayContent = isVoiceOnly ? extractConclusion(data.content) : data.content;
  const agentName = displayName || data.agent || 'Agent';
  const hasContent = displayContent && displayContent.trim().length > 0;
  const [activeLang, setActiveLang] = useState(null);
  const [translatedContent, setTranslatedContent] = useState(null);
  const handleTranslate = (lang, translation) => { setActiveLang(lang); setTranslatedContent(translation); };
  return (
    <div className={`message agent-message phase-${data.phase ?? data.round}`}>
      <div className="message-avatar agent-avatar">{agentName?.charAt(0)?.toUpperCase() || 'A'}</div>
      <div className="message-content">
        <div className="message-meta">
          <span className="agent-name">{agentName}</span>
          <span className="phase-badge">{phaseLabels[data.phase ?? data.round] || data.phase || data.round}</span>
          <MessageActionToolbar
            content={displayContent}
            activeLang={activeLang}
            onTranslate={handleTranslate}
            onSpeak={voiceSettings?.outputMode !== 'text' && onSpeak ? () => onSpeak(displayContent, agentName) : null}
            onSpeakLongPress={voiceSettings?.outputMode !== 'text' && onSpeakLongPress ? () => onSpeakLongPress(displayContent) : null}
            onExportPPTX={onExportPPTX ? () => onExportPPTX(agentName, data.content) : null}
            onGenerateImage={() => onGenerateImage?.(hasContent ? displayContent.slice(0, 500) : '', { messageIndex })}
            t={t}
          />
        </div>
        <TranslateableContent
          content={displayContent}
          translatedContent={translatedContent}
          activeLang={activeLang}
          messageIndex={messageIndex}
          generatedImages={generatedImages}
          onGenerateImage={onGenerateImage}
          t={t}
        />
        <MessageRating messageIndex={messageIndex} ratings={ratings || {}} onRate={onRate || (() => {})} t={t} />
      </div>
    </div>
  );
}

function StreamingAgentMessage({ data, displayName, phaseLabels }) {
  const agentName = displayName || data.name || data.agent || 'Agent';
  const displayContent = data.content || '';
  return (
    <div className={`message agent-message streaming-agent phase-${data.phase ?? data.round}`}>
      <div className="message-avatar agent-avatar">{agentName?.charAt(0)?.toUpperCase() || 'A'}</div>
      <div className="message-content">
        <div className="message-meta">
          <span className="agent-name">{agentName}</span>
          <span className="phase-badge">{phaseLabels[data.phase ?? data.round] || data.phase || data.round}</span>
          <span className="typing-indicator" aria-label="thinking">
            <span></span><span></span><span></span>
          </span>
        </div>
        <div className="streaming-content">
          <MarkdownWithDxf>{displayContent}</MarkdownWithDxf>
        </div>
      </div>
    </div>
  );
}

function FinalAnswer({ data, phaseLabels, onSpeak, onSpeakLongPress, voiceSettings, onExportPPTX, messageIndex, generatedImages, onGenerateImage, ratings, onRate }) {
  const { t } = useI18n();
  const hasContent = data.content && data.content.trim().length > 0;
  const [activeLang, setActiveLang] = useState(null);
  const [translatedContent, setTranslatedContent] = useState(null);
  const handleTranslate = (lang, translation) => { setActiveLang(lang); setTranslatedContent(translation); };
  return (
    <div className="message final-answer">
      <div className="message-avatar final-avatar">✓</div>
      <div className="message-content">
        <div className="message-meta">
          <span className="final-label">{phaseLabels.final || t('finalAnswer')}</span>
          <MessageActionToolbar
            content={data.content}
            activeLang={activeLang}
            onTranslate={handleTranslate}
            onSpeak={voiceSettings?.outputMode !== 'text' && onSpeak ? () => onSpeak(data.content, t('finalAnswer') || 'Synthesizer') : null}
            onSpeakLongPress={voiceSettings?.outputMode !== 'text' && onSpeakLongPress ? () => onSpeakLongPress(data.content) : null}
            onExportPPTX={onExportPPTX ? () => onExportPPTX(phaseLabels.final || t('finalAnswer'), data.content) : null}
            onGenerateImage={() => onGenerateImage?.(hasContent ? data.content.slice(0, 500) : '', { messageIndex })}
            t={t}
          />
        </div>
        <TranslateableContent
          content={data.content}
          translatedContent={translatedContent}
          activeLang={activeLang}
          messageIndex={messageIndex}
          generatedImages={generatedImages}
          onGenerateImage={onGenerateImage}
          t={t}
        />
        <MessageRating messageIndex={messageIndex} ratings={ratings || {}} onRate={onRate || (() => {})} t={t} />
        <MindMapButton content={data.content} />
      </div>
    </div>
  );
}

function MindMapButton({ content }) {
  const { t } = useI18n();
  const [generating, setGenerating] = useState(false);
  const [showMindMap, setShowMindMap] = useState(false);
  const [mindMapData, setMindMapData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const progressInterval = useRef(null);
  
  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    setProgress(0);
    setStatusText(t('analyzingContent'));
    
    // Simulate progress while waiting
    progressInterval.current = setInterval(() => {
      setProgress(prev => {
        if (prev < 30) {
          setStatusText(t('analyzingContent'));
          return prev + 2;
        } else if (prev < 60) {
    setStatusText(t('generatingMindMapStructure'));
          return prev + 1.5;
        } else if (prev < 85) {
    setStatusText(t('organizingBranches'));
          return prev + 1;
        } else if (prev < 95) {
    setStatusText(t('finalizing'));
          return prev + 0.5;
        }
        return prev;
      });
    }, 200);
    
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/mindmap`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: content, model: 'claude:claude-opus-4-8' })
      });
      const data = await res.json();
      
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      
      if (data.success) {
        setProgress(100);
    setStatusText(t('complete'));
        setTimeout(() => {
          setMindMapData(data.mindMap);
          setShowMindMap(true);
          setGenerating(false);
          setProgress(0);
          setStatusText('');
        }, 300);
      } else {
        setProgress(0);
        setStatusText('');
        alert(t('failedToGenerateMindMap') + ': ' + (data.error || 'Unknown error'));
        setGenerating(false);
      }
    } catch (err) {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      setProgress(0);
      setStatusText('');
      alert('Error generating mind map: ' + err.message);
      setGenerating(false);
    }
  };
  
  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, []);
  
  return (
    <>
      {generating ? (
        <div className="mindmap-loading-container">
          <div className="mindmap-progress-bar">
            <div className="mindmap-progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <span className="mindmap-status-text">{statusText} {Math.round(progress)}%</span>
        </div>
      ) : (
        <button 
          className="mindmap-btn" 
          onClick={handleGenerate}
          disabled={generating}
        >
          🧠 {t('mindMap')}
        </button>
      )}
      <MindMapModal 
        isOpen={showMindMap} 
        onClose={() => setShowMindMap(false)} 
        mindMapData={mindMapData}
        sourceText={content}
      />
    </>
  );
}

function TranslateableContent({ content, translatedContent, activeLang, messageIndex, generatedImages, onGenerateImage, t }) {
  const displayContent = activeLang && translatedContent ? translatedContent : content;
  return activeLang === 'ar' ? (
    <div dir="rtl" lang="ar">
      <RenderContent content={displayContent} messageIndex={messageIndex} generatedImages={generatedImages} onGenerateImage={onGenerateImage} t={t} />
    </div>
  ) : (
    <RenderContent content={displayContent} messageIndex={messageIndex} generatedImages={generatedImages} onGenerateImage={onGenerateImage} t={t} />
  );
}

function TranslateButtons({ content, activeLang, onTranslate }) {
  const { t } = useI18n();
  const [translating, setTranslating] = useState(false);

  const handleTranslate = async (lang) => {
    if (activeLang === lang) {
      onTranslate(null, null);
      return;
    }
    setTranslating(true);
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: content, targetLang: lang })
      });
      const data = await res.json();
      if (data.success) {
        onTranslate(lang, data.translation);
      } else {
        alert(t('translationFailed') || 'Translation failed');
      }
    } catch (err) {
      alert((t('translationFailed') || 'Translation failed') + ': ' + err.message);
    }
    setTranslating(false);
  };

  return (
    <>
      <button
        className={`speak-btn ${activeLang === 'fr' ? 'active' : ''}`}
        onClick={() => handleTranslate('fr')}
        disabled={translating}
        title={t('translateToFrench') || 'Traduire en français'}
      >
        FR
      </button>
      <button
        className={`speak-btn ${activeLang === 'ar' ? 'active' : ''}`}
        onClick={() => handleTranslate('ar')}
        disabled={translating}
        title={t('translateToArabic') || 'Traduire en arabe'}
      >
        AR
      </button>
    </>
  );
}

/**
 * Barre d'actions commune pour les réponses d'agent et la réponse finale.
 * Elle est rendue deux fois (en-tête sur desktop, pied de réponse sur mobile)
 * mais une seule instance est visible selon la media-query CSS.
 */
function MessageActionToolbar({
  content,
  activeLang,
  onTranslate,
  onSpeak,
  onSpeakLongPress,
  onExportPPTX,
  onGenerateImage,
  t,
  className
}) {
  const longPressTimerRef = useRef(null);
  const [longPressTriggered, setLongPressTriggered] = useState(false);

  const startLongPress = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLongPressTriggered(false);
    longPressTimerRef.current = setTimeout(() => {
      setLongPressTriggered(true);
      onSpeakLongPress?.(content);
    }, 600);
  };

  const endLongPress = (e) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleClick = (e) => {
    if (longPressTriggered) {
      setLongPressTriggered(false);
      return;
    }
    onSpeak?.();
  };

  return (
    <span className={`message-action-toolbar ${className || ''}`}>
      <TranslateButtons content={content} activeLang={activeLang} onTranslate={onTranslate} />
      {onSpeak && (
        <button
          className="speak-btn"
          onClick={handleClick}
          onTouchStart={startLongPress}
          onTouchEnd={endLongPress}
          onTouchMove={endLongPress}
          onMouseDown={startLongPress}
          onMouseUp={endLongPress}
          onMouseLeave={endLongPress}
          onContextMenu={(e) => e.preventDefault()}
          title={t('readAloud') || 'Lire à haute voix'}
        >
          🔊
        </button>
      )}
      {onExportPPTX && (
        <button className="speak-btn" onClick={onExportPPTX} title={t('exportPPTX') || 'Export PPTX'}>
          📊
        </button>
      )}
      {onGenerateImage && (
        <button className="speak-btn" onClick={onGenerateImage} title={t('generateImage') || 'Générer une image / schéma'}>
          🖼️
        </button>
      )}
    </span>
  );
}

function ErrorMessage({ content }) {
  const { t } = useI18n();
  return (
    <div className="message error-message">
      <div className="message-avatar error-avatar">!</div>
      <div className="message-content">
        <div className="message-meta">
          <span className="error-label">{t('error')}</span>
        </div>
        <p>{content}</p>
      </div>
    </div>
  );
}

function ImageMessage({ url, prompt, alt, onDelete, onRegenerate, onExportPPTX, onExportPDF }) {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="message image-message">
      <div className="message-avatar image-avatar">🖼️</div>
      <div className="message-content">
        <div className="message-meta">
          <span className="image-label">{t('generatedImage') || 'Image générée'}</span>
          <div className="image-actions-menu" ref={menuRef}>
            <button
              className="image-menu-toggle"
              onClick={() => setMenuOpen(o => !o)}
              title={t('actions') || 'Actions'}
              aria-label={t('actions') || 'Actions'}
            >
              ⋮
            </button>
            {menuOpen && (
              <div className="image-menu-dropdown">
                <button onClick={() => { onRegenerate?.(); setMenuOpen(false); }} title={t('regenerateImage') || 'Regénérer l\'image'}>
                  🔄 {t('regenerateImage') || 'Regénérer'}
                </button>
                <button onClick={() => { onExportPPTX?.(); setMenuOpen(false); }} title={t('exportImagePPTX') || 'Exporter en PPTX'}>
                  📊 {t('exportImagePPTX') || 'Exporter PPTX'}
                </button>
                <button onClick={() => { onExportPDF?.(); setMenuOpen(false); }} title={t('exportImagePDF') || 'Exporter en PDF'}>
                  📄 {t('exportImagePDF') || 'Exporter PDF'}
                </button>
                <button className="image-menu-delete" onClick={() => { onDelete?.(); setMenuOpen(false); }} title={t('deleteImage') || 'Supprimer l\'image'}>
                  🗑️ {t('deleteImage') || 'Supprimer'}
                </button>
              </div>
            )}
          </div>
        </div>
        <img src={url} alt={alt || 'Generated'} className="generated-image" />
        {(alt || prompt) && (
          <div className="image-caption">{alt || prompt}</div>
        )}
      </div>
    </div>
  );
}

function ChatMessage({ data, isOwn, isEditing, editText, onEditStart, onEditChange, onEditSave, onEditCancel, onDelete }) {
  const { t } = useI18n();
  return (
    <div className={`message chat-message ${isOwn ? 'chat-you' : 'chat-other'}`}>
      <div className="message-avatar chat-avatar">{data.name?.charAt(0)?.toUpperCase() || '?'}</div>
      <div className="message-content">
        <div className="message-meta">
          <span className="chat-from">{data.name || data.from}</span>
          {isOwn && !isEditing && (
            <span className="message-actions">
              <button className="msg-action-btn" onClick={onEditStart} title={t('edit')}>✏️</button>
              <button className="msg-action-btn" onClick={onDelete} title={t('delete')}>🗑️</button>
            </span>
          )}
        </div>
        {isEditing ? (
          <div className="msg-edit-box">
            <input type="text" value={editText} onChange={(e) => onEditChange(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onEditSave()} autoFocus />
            <div className="msg-edit-actions">
              <button className="btn-primary btn-small" onClick={onEditSave}>{t('save')}</button>
              <button className="btn-secondary btn-small" onClick={onEditCancel}>{t('cancel')}</button>
            </div>
          </div>
        ) : (
          <p>{data.text || data.content}{data.editedAt && <span className="edited-mark"> ({t('edited')})</span>}</p>
        )}
      </div>
    </div>
  );
}

function ModerationMessage({ data }) {
  const { t } = useI18n();
  const text = data.i18nKey ? t(data.i18nKey, data.params) : data.message;
  return (
    <div className="message moderation-message">
      <div className="message-avatar moderation-avatar">🛡️</div>
      <div className="message-content">
        <div className="message-meta">
          <span className="moderation-label">{t('moderator')}</span>
        </div>
        <p>{text}</p>

      </div>
    </div>
  );
}

function HumanInterventionMessage({ data, isOwn, isEditing, editText, onEditStart, onEditChange, onEditSave, onEditCancel, onDelete }) {
  const { t } = useI18n();
  return (
    <div className="message human-intervention-message">
      <div className="message-avatar human-avatar">👤</div>
      <div className="message-content">
        <div className="message-meta">
          <span className="human-label">{data.name} — {t('humanIntervention')}</span>
          {isOwn && !isEditing && (
            <span className="message-actions">
              <button className="msg-action-btn" onClick={onEditStart} title={t('edit')}>✏️</button>
              <button className="msg-action-btn" onClick={onDelete} title={t('delete')}>🗑️</button>
            </span>
          )}
        </div>
        {isEditing ? (
          <div className="msg-edit-box">
            <input type="text" value={editText} onChange={(e) => onEditChange(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onEditSave()} autoFocus />
            <div className="msg-edit-actions">
              <button className="btn-primary btn-small" onClick={onEditSave}>{t('save')}</button>
              <button className="btn-secondary btn-small" onClick={onEditCancel}>{t('cancel')}</button>
            </div>
          </div>
        ) : (
          <p>{data.text}{data.editedAt && <span className="edited-mark"> ({t('edited')})</span>}</p>
        )}
      </div>
    </div>
  );
}
